import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import {
  User,
  getUserStatusBadgeVariant,
  getUserRoleBadgeVariant,
  formatUserType,
} from '../../Data/Users';
import userService from '../../../components/User/services/userService';
import { showError } from '../../../Utilities/sweetAlert';
import { COLORS } from '../../constants/colors';
import './UserDetails.css';

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<{
    hikes: {
      completed: any[];
      incomplete: any[];
    };
    subscription: {
      isPremium: boolean;
      premiumActivatedAt?: Date;
      premiumExpiresAt?: Date;
      daysRemaining?: number;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Invalid user ID');
      setLoading(false);
      return;
    }

    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const [userResponse, detailsResponse] = await Promise.all([
          userService.getUserById(parseInt(id, 10)),
          userService.getUserDetails(parseInt(id, 10))
        ]);

        // Clean user object - remove any object fields that shouldn't be rendered
        const user = userResponse.data.user as any;
        if (user.totalHikeTime && typeof user.totalHikeTime === 'object') {
          delete user.totalHikeTime;
        }

        // Clean hikes - ensure duration is always a string
        const details = detailsResponse.data as any;
        if (details.hikes) {
          ['completed', 'incomplete'].forEach((status) => {
            if (details.hikes[status]) {
              details.hikes[status] = details.hikes[status].map((hike: any) => {
                if (hike.duration && typeof hike.duration === 'object') {
                  return { ...hike, duration: '' };
                }
                return hike;
              });
            }
          });
        }

        setUser(user);
        setUserDetails(details);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching user details:', err);
        setError('Failed to fetch user details');
        showError('Failed to fetch user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  if (loading) {
    return (
      <Container fluid className="user-details-container py-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading user details...</p>
        </div>
      </Container>
    );
  }

  if (error || !user) {
    return (
      <Container fluid className="user-details-container py-4">
        <Alert variant="danger">
          {error || 'User not found'}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/users')}>
          Back to Users
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="user-details-container py-4">
      <Row className="mb-4">
        <Col>
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0" style={{ color: COLORS.WHITE }}>User Details</h4>
                  <small style={{ color: COLORS.WHITE, opacity: 0.9 }}>
                    View detailed information about {user.name}
                  </small>
                </div>
                <Button
                  variant="light"
                  onClick={() => navigate('/users')}
                  className="d-flex align-items-center gap-2"
                  size="sm"
                >
                  <i className="fas fa-arrow-left"></i>
                  Back to Users
                </Button>
              </div>
            </Card.Header>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Profile Information</h5>
            </Card.Header>
            <Card.Body>
              {user.photo && (
                <div className="text-center mb-3">
                  <img
                    src={user.photo}
                    alt={user.name}
                    className="img-fluid rounded"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
              )}
              <Table borderless>
                <tbody>
                  <tr>
                    <td><strong>Name:</strong></td>
                    <td>{user.name}</td>
                  </tr>
                  <tr>
                    <td><strong>Username:</strong></td>
                    <td>@{user.username}</td>
                  </tr>
                  <tr>
                    <td><strong>Email:</strong></td>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <td><strong>Phone:</strong></td>
                    <td>{user.phoneNumber || 'Not provided'}</td>
                  </tr>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>
                      <Badge bg={getUserStatusBadgeVariant(user.status)}>
                        {user.status}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Role:</strong></td>
                    <td>
                      <Badge bg={getUserRoleBadgeVariant(user.role)}>
                        {user.role}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>User Type:</strong></td>
                    <td>
                      <Badge bg={user.email.includes('guest') ? 'secondary' : 'info'}>
                        {formatUserType(user.email)}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Joined:</strong></td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td><strong>FCM Tokens:</strong></td>
                    <td>
                      {user.fcmTokens && user.fcmTokens.length > 0 ? (
                        <>
                          <div className="mb-1">
                            <Badge bg="primary">{user.fcmTokens.length} tokens</Badge>
                          </div>

                          <div
                            style={{
                              maxHeight: '150px',
                              overflowY: 'auto',
                              background: '#f8f9fa',
                              border: '1px solid #ddd',
                              padding: '8px 12px',
                              borderRadius: '6px',
                            }}
                          >
                            {user.fcmTokens.map((token, index) => (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-start mb-2"
                                style={{ borderBottom: '1px solid #eee', paddingBottom: '6px' }}
                              >
                                <small
                                  style={{
                                    wordBreak: 'break-all',
                                    color: '#555',
                                    marginRight: '8px',
                                    flex: 1,
                                  }}
                                >
                                  {token}
                                </small>

                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => navigator.clipboard.writeText(token)}
                                >
                                  Copy
                                </Button>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted">No FCM tokens</span>
                      )}
                    </td>
                  </tr>

                  {user.emergencyContactName && (
                    <tr>
                      <td><strong>Emergency Contact:</strong></td>
                      <td>
                        {user.emergencyContactName}
                        {user.emergencyContactNumber && (
                          <><br /><small className="text-muted">{user.emergencyContactNumber}</small></>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {userDetails && (
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Subscription</h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    <tr>
                      <td><strong>Premium Status:</strong></td>
                      <td>
                        <Badge
                          style={{
                            backgroundColor: userDetails.subscription.isPremium ? COLORS.PRIMARY_ORANGE : COLORS.LIGHT_GREY,
                            color: COLORS.WHITE
                          }}
                        >
                          {userDetails.subscription.isPremium ? 'Premium' : 'Free'}
                        </Badge>
                        {userDetails.subscription.daysRemaining !== undefined && userDetails.subscription.daysRemaining > 0 && (
                          <span className="ms-2 text-muted">
                            ({userDetails.subscription.daysRemaining} days remaining)
                          </span>
                        )}
                      </td>
                    </tr>
                    {userDetails.subscription.premiumActivatedAt && (
                      <tr>
                        <td><strong>Premium Since:</strong></td>
                        <td>{new Date(userDetails.subscription.premiumActivatedAt).toLocaleDateString()}</td>
                      </tr>
                    )}
                    {userDetails.subscription.premiumExpiresAt && (
                      <tr>
                        <td><strong>Premium Expires:</strong></td>
                        <td>{new Date(userDetails.subscription.premiumExpiresAt).toLocaleDateString()}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

        </Col>

        <Col md={8}>
          {userDetails && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">Hikes</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header style={{ backgroundColor: COLORS.BUTTON_GREEN, color: COLORS.WHITE }}>
                        <strong>Completed ({userDetails.hikes.completed.length})</strong>
                      </Card.Header>
                      <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {userDetails.hikes.completed.length > 0 ? (
                          <ul className="list-unstyled mb-0">
                            {userDetails.hikes.completed.map((hike) => (
                              <li key={hike.id} className="mb-2 pb-2 border-bottom">
                                <strong>{hike.trailName}</strong><br />
                                <small className="text-muted">
                                  {hike.distance} km • {hike.duration}
                                  {hike.createdAt && ` • ${new Date(hike.createdAt).toLocaleDateString()}`}
                                </small>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted mb-0">No completed hikes</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3">
                      <Card.Header style={{ backgroundColor: COLORS.ERROR_RED, color: COLORS.WHITE }}>
                        <strong>Incomplete ({userDetails.hikes.incomplete.length})</strong>
                      </Card.Header>
                      <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {userDetails.hikes.incomplete.length > 0 ? (
                          <ul className="list-unstyled mb-0">
                            {userDetails.hikes.incomplete.map((hike) => (
                              <li key={hike.id} className="mb-2 pb-2 border-bottom">
                                <strong>{hike.trailName}</strong><br />
                                <small className="text-muted">
                                  {hike.distance} km • {hike.duration}
                                  {hike.createdAt && ` • ${new Date(hike.createdAt).toLocaleDateString()}`}
                                </small>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted mb-0">No incomplete hikes</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {user.onboardingAnswers && user.onboardingAnswers.length > 0 && (
            <Card className="mt-4">
              <Card.Header>
                <h5 className="mb-0">Onboarding Answers</h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    {user.onboardingAnswers.map((item, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="py-3" style={{ width: '40%' }}><strong>{item.question}</strong></td>
                        <td className="py-3">{item.answer}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default UserDetails;

